import React from 'react';

import {Angle, Point} from 'util/geometry/GeometryConstants';
import {polarToCartesian, toStandardAngle} from 'util/geometry/GeometryUtil';

export interface ArcTextProps {
  circleCenter: Point;
  radius: number;
  startAngle: Angle;
  endAngle: Angle;
  text: string;
  id: string;
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: number;
  color?: string;
}

/**
 * Renders text along a curved path on a circle arc.
 *
 * These angles are in radians, relative to 0 at the top of the circle.
 * The arc that text is rendered on is the arc from startAngle to endAngle going clockwise,
 * and the text is always right-side-up and centered on the arc.
 */
export default function ArcText({
  circleCenter,
  radius,
  startAngle,
  endAngle,
  text,
  id,
  fontFamily,
  fontWeight,
  fontSize,
  color,
}: ArcTextProps): JSX.Element {
  const standardStart = toStandardAngle(startAngle);
  const standardEnd = toStandardAngle(endAngle);
  let middleAngle = (standardStart + standardEnd) / 2;
  if (standardStart > standardEnd) {
    middleAngle -= Math.PI;
  }
  const largeArc =
    standardEnd > standardStart
      ? standardEnd - standardStart >= Math.PI
      : 2 * Math.PI - (standardStart - standardEnd) >= Math.PI;

  let arcStartAngle = standardStart;
  let arcEndAngle = standardEnd;
  let sweepFlag = true;
  // If the arc center is in the bottom half of the circle,
  // flip the arc so that it is rendered from left to right and the text is not upside down.
  if (Math.PI / 2 < middleAngle && middleAngle < (3 * Math.PI) / 2) {
    arcStartAngle = standardEnd;
    arcEndAngle = standardStart;
    sweepFlag = false;
  }
  const start = polarToCartesian(circleCenter, radius, arcStartAngle);
  const end = polarToCartesian(circleCenter, radius, arcEndAngle);

  const arcPath = [
    'M',
    start.x,
    start.y,
    'A',
    radius,
    radius,
    0, // No x-axis rotation
    largeArc ? 1 : 0,
    sweepFlag ? 1 : 0,
    end.x,
    end.y,
  ].join(' ');

  return (
    <g>
      <path id={id} d={arcPath} fill="none" />
      <text
        fontFamily={fontFamily}
        fontWeight={fontWeight}
        fontSize={fontSize}
        color={color}
        dominantBaseline="middle"
        textAnchor="middle"
      >
        <textPath xlinkHref={`#${id}`} startOffset="50%">
          {text}
        </textPath>
      </text>
    </g>
  );
}
